/**
 * CLI Release QA Management
 *
 * Trigger, poll, and check status of Release QA runs from CI/CD pipelines.
 * Used by the scheduled-release GitHub Actions workflow to gate deployments.
 */

import process from "node:process";
import { resolveConfig, getArgValue, hasFlag } from "./auth.js";
import { getApplicationIdArg } from "./cli-helpers.js";

type TriggerResponse = {
  ok: boolean;
  releaseRunId?: string;
  jobId?: string;
  error?: string;
};

type RunStatusResponse = {
  ok: boolean;
  run?: {
    id: string;
    status: string;
    triggerSource: string;
    cutoffReason: string | null;
    fromSha: string | null;
    toSha: string | null;
    commitsAnalyzed: number;
    testersSpawned: number;
    testersCompleted: number;
    testersFailed: number;
    issuesFound: number;
    regressionTestsTotal: number;
    regressionTestsPassed: number;
    regressionTestsFailed: number;
    startedAt: string | null;
    finishedAt: string | null;
    createdAt: string;
    // Multi-repo: one entry per participating repo. Empty array for
    // single-repo/legacy runs; consumers fall back to top-level fromSha/toSha.
    repos?: Array<{
      repoFullName: string;
      fromSha: string;
      toSha: string;
    }>;
  };
  error?: string;
};

/** Terminal statuses that stop polling */
const TERMINAL_STATUSES = new Set([
  "completed",
  "completed_with_errors",
  "failed",
  "canceled",
  "timeout",
]);

function getReleaseWorkstreams(argv: string[]) {
  const skipRegressions = hasFlag(argv, "--skip-regressions");
  const regressionsOnly = hasFlag(argv, "--regressions-only");

  if (skipRegressions && regressionsOnly) {
    console.error("Error: --skip-regressions and --regressions-only cannot be used together.");
    process.exit(1);
  }

  const workstreams = {
    runRegressions: regressionsOnly || !skipRegressions,
    planNewTests: regressionsOnly ? false : !hasFlag(argv, "--skip-planning"),
    generateReleaseNotes: regressionsOnly ? false : !hasFlag(argv, "--skip-release-notes"),
  };

  if (!workstreams.runRegressions && !workstreams.planNewTests && !workstreams.generateReleaseNotes) {
    console.error("Error: Select at least one release workstream.");
    process.exit(1);
  }

  return { workstreams, skipRegressions, regressionsOnly };
}

async function handleTrigger(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = getApplicationIdArg(argv);
  if (!propertyId) {
    console.error("Error: Missing --application-id <uuid> (alias: --property-id).");
    console.error(
      "Usage: cofactor release trigger --application-id <uuid> [--credential-ids <uuid,...>] [--regressions-only]"
    );
    process.exit(1);
  }

  const { workstreams, skipRegressions, regressionsOnly } = getReleaseWorkstreams(argv);
  const credentialIdsRaw = getArgValue(argv, "--credential-ids");
  const body: Record<string, unknown> = { propertyId, ...workstreams };
  if (credentialIdsRaw) {
    body.credentialIds = credentialIdsRaw.split(",").map((id) => id.trim());
  }

  if (skipRegressions) {
    console.warn("WARNING: --skip-regressions is set. The regression suite will NOT run.");
  } else if (regressionsOnly) {
    console.log("Running the regression suite only; Planning and release notes are disabled.");
  }

  const res = await fetch(`${apiUrl}/api/v1/release-qa/trigger`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (res.status === 401) {
    console.error("Error: Unauthorized. Check your API token.");
    process.exit(1);
  }

  const json = (await res.json()) as TriggerResponse;

  if (!json.ok) {
    console.error(`Error: ${json.error}`);
    process.exit(1);
  }

  console.log(`Release QA run triggered`);
  console.log(`  Run ID: ${json.releaseRunId}`);
  console.log(`  Job ID: ${json.jobId}`);
}

async function fetchRunStatus(
  apiUrl: string,
  token: string,
  runId: string
): Promise<RunStatusResponse> {
  const res = await fetch(`${apiUrl}/api/v1/release-qa/runs/${encodeURIComponent(runId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (res.status === 401) {
    console.error("Error: Unauthorized. Check your API token.");
    process.exit(1);
  }

  return (await res.json()) as RunStatusResponse;
}

async function handleStatus(argv: string[], apiUrl: string, token: string): Promise<void> {
  const runId = argv[0];
  if (!runId || runId.startsWith("--")) {
    console.error("Error: Missing run ID.");
    console.error("Usage: canary release status <run-id>");
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, "--json");
  const json = await fetchRunStatus(apiUrl, token, runId);

  if (!json.ok) {
    console.error(`Error: ${json.error}`);
    process.exit(1);
  }

  const run = json.run!;

  if (jsonOutput) {
    console.log(JSON.stringify(run, null, 2));
    return;
  }

  console.log(`Release QA Run: ${run.id}`);
  console.log(`  Status:              ${run.status}`);
  console.log(`  Trigger:             ${run.triggerSource}`);
  if (run.repos && run.repos.length > 0) {
    console.log(`  Repositories:`);
    for (const r of run.repos) {
      const range =
        r.fromSha === r.toSha
          ? "(no changes)"
          : `${r.fromSha.slice(0, 7)}..${r.toSha.slice(0, 7)}`;
      console.log(`    - ${r.repoFullName}  ${range}`);
    }
  }
  console.log(`  Commits analyzed:    ${run.commitsAnalyzed}`);
  console.log(`  Testers:             ${run.testersCompleted}/${run.testersSpawned} completed, ${run.testersFailed} failed`);
  console.log(`  Issues found:        ${run.issuesFound}`);
  console.log(`  Regression tests:    ${run.regressionTestsPassed}/${run.regressionTestsTotal} passed, ${run.regressionTestsFailed} failed`);
  if (run.startedAt) console.log(`  Started:             ${run.startedAt}`);
  if (run.finishedAt) console.log(`  Finished:            ${run.finishedAt}`);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function handleRun(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = getApplicationIdArg(argv);
  if (!propertyId) {
    console.error("Error: Missing --application-id <uuid> (alias: --property-id).");
    console.error(
      "Usage: cofactor release run --application-id <uuid> [--timeout 3600] [--poll-interval 30] [--regressions-only]"
    );
    process.exit(1);
  }

  const timeoutSec = parseInt(getArgValue(argv, "--timeout") ?? "3600", 10);
  const pollIntervalSec = parseInt(getArgValue(argv, "--poll-interval") ?? "30", 10);
  const { workstreams, skipRegressions, regressionsOnly } = getReleaseWorkstreams(argv);

  const credentialIdsRaw = getArgValue(argv, "--credential-ids");
  const body: Record<string, unknown> = { propertyId, ...workstreams };
  if (credentialIdsRaw) {
    body.credentialIds = credentialIdsRaw.split(",").map((id) => id.trim());
  }

  if (skipRegressions) {
    console.warn("WARNING: --skip-regressions is set. The regression suite will NOT run.");
  } else if (regressionsOnly) {
    console.log("Running the regression suite only; Planning and release notes are disabled.");
  }

  // Trigger
  console.log("Triggering Release QA run...");
  const triggerRes = await fetch(`${apiUrl}/api/v1/release-qa/trigger`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (triggerRes.status === 401) {
    console.error("Error: Unauthorized. Check your API token.");
    process.exit(1);
  }

  const triggerJson = (await triggerRes.json()) as TriggerResponse;

  if (!triggerJson.ok) {
    console.error(`Error triggering run: ${triggerJson.error}`);
    process.exit(1);
  }

  const runId = triggerJson.releaseRunId!;
  console.log(`Run started: ${runId}`);

  // Poll
  const deadline = Date.now() + timeoutSec * 1000;
  let lastStatus = "";

  while (Date.now() < deadline) {
    await sleep(pollIntervalSec * 1000);

    let statusJson: RunStatusResponse;
    try {
      statusJson = await fetchRunStatus(apiUrl, token, runId);
    } catch (err) {
      console.error(`Warning: Failed to fetch status, retrying... (${err})`);
      continue;
    }

    if (!statusJson.ok) {
      console.error(`Error: ${statusJson.error}`);
      process.exit(1);
    }

    const run = statusJson.run!;
    const statusLine = `[${run.status}] testers: ${run.testersCompleted}/${run.testersSpawned}, regressions: ${run.regressionTestsPassed}/${run.regressionTestsTotal} passed`;

    if (run.status !== lastStatus) {
      console.log(statusLine);
      lastStatus = run.status;
    } else {
      // Print progress on same status change in metrics
      console.log(statusLine);
    }

    if (TERMINAL_STATUSES.has(run.status)) {
      console.log("");
      console.log(`Run finished: ${run.status}`);
      console.log(`  Testers: ${run.testersCompleted}/${run.testersSpawned} completed, ${run.testersFailed} failed`);
      console.log(`  Regressions: ${run.regressionTestsPassed}/${run.regressionTestsTotal} passed, ${run.regressionTestsFailed} failed`);
      console.log(`  Issues found: ${run.issuesFound}`);

      // Gate deployment on: (a) terminal status, (b) any regression failures,
      // (c) empty regression suite (unless explicitly opted out).
      // Non-regression signals (failed testers, issues, build errors) are
      // visible in the UI but intentionally do not gate the deploy.
      if (run.status === "failed" || run.status === "canceled" || run.status === "timeout") {
        console.error(`\nRelease QA FAILED (${run.status})`);
        process.exit(1);
      }

      if (run.status === "completed_with_errors") {
        const suffix = run.cutoffReason ? `: ${run.cutoffReason}` : "";
        console.error(`\nRelease QA FAILED (completed_with_errors${suffix})`);
        process.exit(1);
      }

      if (run.regressionTestsTotal === 0 && !skipRegressions) {
        console.error(
          "\nRelease QA FAILED (no regression tests executed — expected suite to run). " +
            "If this was intentional, re-run with --skip-regressions."
        );
        process.exit(1);
      }

      if (run.regressionTestsFailed === 0) {
        console.log(
          `\nRelease QA PASSED (${run.regressionTestsPassed}/${run.regressionTestsTotal} regressions passed)`
        );
        process.exit(0);
      }

      console.error(`\nRelease QA FAILED (${run.regressionTestsFailed} regression test(s) failed)`);
      process.exit(1);
    }
  }

  console.error(`\nTimeout: Release QA did not complete within ${timeoutSec}s`);
  process.exit(1);
}

// ---------------------------------------------------------------------------
// Deployment marking
// ---------------------------------------------------------------------------

type MarkDeployResponse = {
  ok: boolean;
  deploymentId?: string;
  commitSha?: string;
  tasksTriggered?: number;
  error?: string;
};

type LatestDeployResponse = {
  ok: boolean;
  deployment?: {
    id: string;
    commitSha: string;
    commitMessage: string | null;
    deployedBy: string;
    triggerSource: string;
    tasksTriggered: number;
    createdAt: string;
  } | null;
  error?: string;
};

async function handleMarkDeploy(argv: string[], apiUrl: string, token: string): Promise<void> {
  const commitSha = getArgValue(argv, "--commit");
  const propertyId = getApplicationIdArg(argv);
  const commitMessage = getArgValue(argv, "--message");

  if (!commitSha) {
    console.error("Error: Missing --commit <sha>.");
    console.error("Usage: canary release mark-deploy --commit <sha> --application-id <uuid> [--message \"...\"]");
    process.exit(1);
  }
  if (!propertyId) {
    console.error("Error: Missing --application-id <uuid> (alias: --property-id).");
    console.error("Usage: canary release mark-deploy --commit <sha> --application-id <uuid> [--message \"...\"]");
    process.exit(1);
  }

  const body: Record<string, unknown> = { propertyId, commitSha };
  if (commitMessage) body.commitMessage = commitMessage;

  const res = await fetch(`${apiUrl}/api/v1/deployments/mark`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (res.status === 401) {
    console.error("Error: Unauthorized. Check your API token.");
    process.exit(1);
  }

  const json = (await res.json()) as MarkDeployResponse;

  if (!json.ok) {
    console.error(`Error: ${json.error}`);
    process.exit(1);
  }

  console.log("Deployment marked");
  console.log(`  Deployment ID:    ${json.deploymentId}`);
  console.log(`  Commit:           ${json.commitSha}`);
  console.log(`  Tasks triggered:  ${json.tasksTriggered}`);
}

async function handleLatestDeploy(argv: string[], apiUrl: string, token: string): Promise<void> {
  const propertyId = getApplicationIdArg(argv);
  if (!propertyId) {
    console.error("Error: Missing --application-id <uuid> (alias: --property-id).");
    console.error("Usage: canary release latest-deploy --application-id <uuid> [--json]");
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, "--json");

  const res = await fetch(
    `${apiUrl}/api/v1/deployments/latest?propertyId=${encodeURIComponent(propertyId)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (res.status === 401) {
    console.error("Error: Unauthorized. Check your API token.");
    process.exit(1);
  }

  const data = (await res.json()) as LatestDeployResponse;

  if (!data.ok) {
    console.error(`Error: ${data.error}`);
    process.exit(1);
  }

  if (!data.deployment) {
    console.log("No deployments found for this application.");
    return;
  }

  if (jsonOutput) {
    console.log(JSON.stringify(data.deployment, null, 2));
    return;
  }

  console.log(`Latest deployment:`);
  console.log(`  ID:               ${data.deployment.id}`);
  console.log(`  Commit:           ${data.deployment.commitSha}`);
  if (data.deployment.commitMessage) {
    console.log(`  Message:          ${data.deployment.commitMessage}`);
  }
  console.log(`  Deployed by:      ${data.deployment.deployedBy}`);
  console.log(`  Source:            ${data.deployment.triggerSource}`);
  console.log(`  Tasks triggered:  ${data.deployment.tasksTriggered}`);
  console.log(`  Created:          ${data.deployment.createdAt}`);
}

// ---------------------------------------------------------------------------
// Help & routing
// ---------------------------------------------------------------------------

function printReleaseHelp(): void {
  console.log(
    [
      "Usage: canary release <sub-command> [options]",
      "",
      "Sub-commands:",
      "  trigger --application-id <uuid> [--credential-ids <uuid,...>]",
      "                                     Trigger a Release QA run",
      "  status <run-id> [--json]           Check run status",
      "  run --application-id <uuid> [options] Trigger and poll until complete",
      "  mark-deploy --commit <sha> --application-id <uuid> [--message \"...\"]",
      "                                     Mark a commit as deployed",
      "  latest-deploy --application-id <uuid> [--json]",
      "                                     Show latest deployment for an application",
      "",
      "Run options:",
      "  --timeout <seconds>     Max wait time (default: 3600)",
      "  --poll-interval <secs>  Poll frequency (default: 30)",
      "  --credential-ids <ids>  Comma-separated credential UUIDs",
      "  --regressions-only      Run the regression suite without planning or release notes",
      "  --skip-regressions      Skip the regression suite",
      "  --skip-planning         Skip planning tests for code changes",
      "  --skip-release-notes    Skip release notes and documentation updates",
      "",
      "Options:",
      "  --env <env>       Target environment (prod, dev, local)",
      "  --api-url <url>   API URL override",
      "  --token <key>     API token override (or set CANARY_API_TOKEN)",
    ].join("\n")
  );
}

export async function runRelease(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === "help" || hasFlag(argv, "--help", "-h")) {
    printReleaseHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);

  switch (subCommand) {
    case "trigger":
      await handleTrigger(rest, apiUrl, token);
      break;
    case "status":
      await handleStatus(rest, apiUrl, token);
      break;
    case "run":
      await handleRun(rest, apiUrl, token);
      break;
    case "mark-deploy":
      await handleMarkDeploy(rest, apiUrl, token);
      break;
    case "latest-deploy":
      await handleLatestDeploy(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printReleaseHelp();
      process.exit(1);
  }
}
